import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import {
  Box, Paper, Tabs, Tab, Typography, TextField, Button, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Chip
} from '@material-ui/core'
import TemplateEditor from './TemplateEditor'
import AudienceBuilder from './AudienceBuilder'
import CampaignReview from './CampaignReview'
import CampaignHistory from './CampaignHistory'
import {
  listTemplates, createTemplate, updateTemplate, getTemplate,
  createCampaign, getCampaign
} from './campaignsApi'

function CampaignBuilderPage ({ url }) {
  const [tab, setTab] = useState(0)

  // Template state
  const [templates, setTemplates] = useState([])
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [savingTemplate, setSavingTemplate] = useState(false)

  // Audience state
  const [filter, setFilter] = useState({ requireEmailFlags: true, onlyActive: true })

  // Campaign state
  const [campaign, setCampaign] = useState(null)
  const [campaignName, setCampaignName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    listTemplates(url).then(setTemplates).catch(e => setError(e.message))
  }, [url])

  const loadTemplate = async (id) => {
    setSelectedTemplateId(id)
    if (!id) return
    const t = await getTemplate(url, id)
    setTemplateName(t.name || '')
    setSubject(t.subject || '')
    setBodyHtml(t.bodyHtml || '')
  }

  const saveTemplate = async () => {
    setSavingTemplate(true)
    setError(null)
    try {
      const body = { name: templateName || 'Untitled template', subject, bodyHtml, letterheadKey: 'hmi' }
      let saved
      if (selectedTemplateId) saved = await updateTemplate(url, selectedTemplateId, body)
      else saved = await createTemplate(url, body)
      const refreshed = await listTemplates(url)
      setTemplates(refreshed)
      setSelectedTemplateId(saved._id)
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setSavingTemplate(false)
    }
  }

  const createTheCampaign = async () => {
    setCreating(true)
    setError(null)
    try {
      if (!selectedTemplateId) throw new Error('Save or select a template first.')
      const c = await createCampaign(url, {
        name: campaignName || templateName || 'Untitled campaign',
        templateId: selectedTemplateId,
        subject,
        bodyHtml,
        letterheadKey: 'hmi',
        audienceFilter: filter
      })
      setCampaign(c)
      setTab(2)
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally {
      setCreating(false)
    }
  }

  const refreshCampaign = async () => {
    if (campaign?._id) {
      const c = await getCampaign(url, campaign._id)
      setCampaign(c)
    }
  }

  return (
    <Box style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <Typography variant="h5" gutterBottom>Mass Email Campaigns</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Author a template, build a recipient list from Aspire data, preview, test, and send.
      </Typography>

      {error && <Typography color="error" variant="body2" style={{ marginBottom: 8 }}>{error}</Typography>}

      <Paper style={{ marginTop: 16 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="1. Template" />
          <Tab label="2. Audience" />
          <Tab label="3. Review & Send" disabled={!campaign} />
          <Tab label="History" />
        </Tabs>

        <Box style={{ padding: 24 }}>
          {tab === 0 && (
            <Box>
              <Box display="flex" style={{ gap: 12, marginBottom: 16 }}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 240 }}>
                  <InputLabel>Load template</InputLabel>
                  <Select label="Load template" value={selectedTemplateId} onChange={(e) => loadTemplate(e.target.value)}>
                    <MenuItem value=""><em>New template</em></MenuItem>
                    {templates.map(t => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField label="Template name" variant="outlined" size="small"
                  value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
                <Button variant="outlined" onClick={saveTemplate} disabled={savingTemplate}>
                  {savingTemplate ? <CircularProgress size={20} /> : (selectedTemplateId ? 'Update' : 'Save')}
                </Button>
              </Box>
              <TemplateEditor
                subject={subject}
                bodyHtml={bodyHtml}
                onChange={({ subject: s, bodyHtml: b }) => {
                  if (s !== undefined) setSubject(s)
                  if (b !== undefined) setBodyHtml(b)
                }}
              />
              <Box mt={2}>
                <Button variant="contained" color="primary" onClick={() => setTab(1)}>Next: Audience</Button>
              </Box>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <AudienceBuilder url={url} filter={filter} onChange={setFilter} />
              <Box mt={3} display="flex" alignItems="center" style={{ gap: 12 }}>
                <TextField label="Campaign name" variant="outlined" size="small"
                  value={campaignName} onChange={(e) => setCampaignName(e.target.value)} />
                <Button variant="contained" color="primary" onClick={createTheCampaign} disabled={creating}>
                  {creating ? <CircularProgress size={20} /> : 'Create campaign & continue'}
                </Button>
                {!selectedTemplateId && <Chip size="small" label="Save a template first" />}
              </Box>
            </Box>
          )}

          {tab === 2 && campaign && (
            <CampaignReview url={url} campaign={campaign} onStatusChange={refreshCampaign} />
          )}

          {tab === 3 && (
            <CampaignHistory url={url} />
          )}
        </Box>
      </Paper>
    </Box>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  perms: state.perms,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(CampaignBuilderPage)
